// EXTERNAL
import { unzipSync, strFromU8 } from "fflate";

/**
 * JH (30-07-26)
 * Build a self-contained, renderable HTML blob URL from a package .zip so the New Ad "Custom" flow can
 * preview the ad BEFORE it's uploaded to S3. Each packaged asset becomes an object URL and the entry
 * index.html's relative `src`/`href`/`url(...)` references are rewritten to point at them.
 *
 * Limitations (acceptable for a pre-save preview; the post-save preview loads the real S3 files):
 * assets referenced dynamically from JS, or from inside a separate .css file's url(), aren't rewritten.
 * External/CDN refs (e.g. the Picnic design-kit) load directly and are left untouched.
 */

const CONTENT_TYPE: Record<string, string> = {
  html: "text/html",
  js: "text/javascript",
  css: "text/css",
  json: "application/json",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  svg: "image/svg+xml",
  webp: "image/webp",
  mp4: "video/mp4",
  webm: "video/webm",
  woff: "font/woff",
  woff2: "font/woff2",
  ttf: "font/ttf",
  otf: "font/otf",
};

function contentType(path: string): string {
  const ext = path.slice(path.lastIndexOf(".") + 1).toLowerCase();
  return CONTENT_TYPE[ext] || "application/octet-stream";
}

export interface BlobPreview {
  url: string;
  revoke: () => void;
}

export async function buildBlobPreview(file: File): Promise<BlobPreview> {
  const entries = unzipSync(new Uint8Array(await file.arrayBuffer()));
  const created: string[] = [];
  const objectUrl = (data: Uint8Array, type: string) => {
    const url = URL.createObjectURL(new Blob([data], { type }));
    created.push(url);
    return url;
  };

  // Every asset (everything but the entry html) gets an object URL, keyed by its package path.
  const assetUrls: Record<string, string> = {};
  for (const path of Object.keys(entries)) {
    if (path.endsWith("/") || path === "index.html") continue;
    assetUrls[path] = objectUrl(entries[path], contentType(path));
  }

  const rewriteRef = (ref: string): string | null => {
    if (/^(https?:|data:|blob:|#|javascript:)/i.test(ref) || ref.startsWith("/")) return null;
    const clean = ref.split(/[?#]/)[0];
    return assetUrls[clean] || null;
  };

  let html = strFromU8(entries["index.html"] || new Uint8Array());
  // src="..." / href="..."
  html = html.replace(/((?:src|href)\s*=\s*)(["'])([^"']+)\2/gi, (match, attr, quote, ref) => {
    const url = rewriteRef(ref);
    return url ? `${attr}${quote}${url}${quote}` : match;
  });
  // url(...) inside inline styles / <style> blocks
  html = html.replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/gi, (match, quote, ref) => {
    const url = rewriteRef(ref);
    return url ? `url(${quote}${url}${quote})` : match;
  });

  const htmlUrl = objectUrl(new TextEncoder().encode(html), "text/html");
  return { url: htmlUrl, revoke: () => created.forEach((u) => URL.revokeObjectURL(u)) };
}
